import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { makeStyles, Popover } from '@material-ui/core';

import PerformanceChart from '../performance-chart/PerformanceChart';
import { statusDef, annotatedStatusDef } from '../performance-chart/defs';
import useSize from '../../../utilities/UseSize';
import { getPointWidth } from '../data-series';
import { useAnnotationsService } from '../../../services/AnnotationsService';
import assignAnnotations from '../../annotations/AnnotationsHelper';
import { useAreAnnotationsEnabled } from '../../../stores/EnvironmentStore';

const useStyles = makeStyles((theme) => {
    return {
        chart: {
            '& .highcharts-point-hover': {
                opacity: 0.6,
            },
        },
        annotationCard: {
            position: 'float',
            backgroundColor: theme.palette.background.paper,
            bottom: 40,
        },
    };
});

const getColorTrend = (data, color) => {
    return data.map((item) => {
        const worst = item.value?.worst;
        const field = worst || 'gray';
        const value = field === color ? 100 : 0;
        return {
            date: item.date,
            value,
        };
    });
};

const statusToMetrics = (statusTrend) => {
    if (!statusTrend?.data) return undefined;
    const { data, granularity } = statusTrend;

    return {
        trends: {
            gray: {
                data: getColorTrend(data, 'gray'),
                granularity,
            },
            green: {
                data: getColorTrend(data, 'green'),
                granularity,
            },
            yellow: {
                data: getColorTrend(data, 'yellow'),
                granularity,
            },
            orange: {
                data: getColorTrend(data, 'orange'),
                granularity,
            },
            red: {
                data: getColorTrend(data, 'red'),
                granularity,
            },
        },
    };
};

const xAxis = {
    gridLineWidth: 0,
};

export default function StatusChart({ disableTimeAxis, statusTrend, chartData, entity, ...performanceChartProps }) {
    const classes = useStyles();
    const containerRef = useRef();
    const chartRef = useRef();
    const { width } = useSize(containerRef);
    const [pointWidth, setPointwidth] = useState(0);
    const [data, setData] = useState();

    const [isAnnotated] = useAreAnnotationsEnabled();
    const [annotations, setAnnotations] = useState([]);
    const [annotatedChicklets, setAnnotatedChicklets] = useState([]);
    const { granularity } = statusTrend;
    const [annotationCoord, setAnnotationCoord] = useState();
    const [openAnnotation, setOpenAnnotation] = useState(false);
    const [currentAnnotation, setCurrentAnnotation] = useState(null);
    const [indicators, setIndicators] = useState([]);
    const trend = statusTrend.data;
    const annotationsService = useAnnotationsService();

    const clearIndicators = useCallback(() => {
        if (indicators && indicators.length > 0) {
            indicators.forEach((indicator) => indicator.destroy());
            setIndicators([]);
        }
    }, [indicators]);

    useEffect(() => {
        setData(statusToMetrics(statusTrend));
    }, [statusTrend]);

    useEffect(() => {
        if (width && data) {
            const newPointWidth = getPointWidth(statusDef, data, width);
            if (newPointWidth !== pointWidth && newPointWidth) {
                setPointwidth(newPointWidth);
            }
        }
    }, [data, width, pointWidth]);

    // Retrieve annotations from Cassandra
    useEffect(() => {
        if (isAnnotated) {
            annotationsService
                .getAnnotations(entity)
                .then((result) => setAnnotations(result.data))
                .catch(() => setAnnotations([]));
        }
    }, [entity, isAnnotated, annotationsService]);

    // Clear prior indicators of annotations and render current indicators (dynamically update indicators)
    useEffect(() => {
        if (isAnnotated) {
            clearIndicators();
            if (
                chartRef &&
                chartRef.current !== undefined &&
                chartRef.current !== null &&
                annotatedChicklets &&
                annotatedChicklets.length > 0
            ) {
                const svgs = [];
                chartRef.current.chart.series[1].data.forEach((point) => {
                    annotatedChicklets.forEach((note) => {
                        if (note.date === point.category) {
                            const svg = chartRef.current.chart.renderer
                                .circle(point.plotX, point.plotY + chartRef.current.chart.plotBox.height / 2, 2)
                                .add();
                            svg.toFront().on('click', (event) => {
                                const container = containerRef.current.getBoundingClientRect();
                                const y = container.bottom;
                                setAnnotationCoord({ left: event.x, top: y });
                                setCurrentAnnotation(note.annotations[0]);
                            });
                            svgs.push(svg);
                        }
                    });
                });
                // Retain reference to the new set of SVG indicators
                setIndicators(svgs);
            }
        }
    }, [chartRef, isAnnotated, annotatedChicklets, pointWidth, clearIndicators]);

    // assign annotations to each annotated chicklet
    useEffect(() => {
        setAnnotatedChicklets(assignAnnotations(trend, annotations, granularity));
    }, [trend, annotations, granularity]);

    // Handle the opening and closing of the annotation popover element (handleClose, setOpen, and onClick)
    const handleCloseAnnotation = () => {
        setOpenAnnotation(false);
        setCurrentAnnotation(null);
    };

    useEffect(() => {
        setOpenAnnotation(true);
    }, [annotationCoord]);

    const eventHandlers = useMemo(() => {
        if (!annotatedChicklets || annotatedChicklets.length < 1) return {};
        const notes = [...annotatedChicklets];
        return isAnnotated
            ? {
                  onClick: (e) => {
                      const container = containerRef.current.getBoundingClientRect();
                      const x = e.point.clientX + container.left;
                      const y = container.bottom;
                      setAnnotationCoord({ left: x + e.point.pointWidth / 2, top: y });
                      notes.forEach((note) => {
                          if (note.date >= e.pointData.date && note.date < e.pointData.date + e.granularity) {
                              setCurrentAnnotation(note.annotations[0]);
                          }
                      });
                  },
              }
            : {};
    }, [isAnnotated, annotatedChicklets]);

    return (
        <div className="flex-container" ref={containerRef}>
            {pointWidth === 0 ? (
                <div />
            ) : (
                <PerformanceChart
                    className={classes.chart}
                    disableLegend
                    disableYAxis
                    disableTimeAxis={disableTimeAxis}
                    chartDef={isAnnotated ? annotatedStatusDef : statusDef}
                    data={data}
                    pointWidth={pointWidth}
                    xAxis={xAxis}
                    hideLine
                    handleEvents={eventHandlers}
                    chartRef={chartRef}
                    {...performanceChartProps}
                />
            )}
            {isAnnotated && annotationCoord && currentAnnotation && (
                <Popover
                    anchorReference="anchorPosition"
                    anchorPosition={annotationCoord}
                    open={openAnnotation}
                    onClose={handleCloseAnnotation}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    {`${currentAnnotation}`}
                </Popover>
            )}
        </div>
    );
}
